import { CalendarIcon } from "@src/assets/general-icons/CalendarIcon";
import React, { CSSProperties, useState } from "react";
import DatePicker from "react-datepicker";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { clx } from "../utils/stringUtils";
import styles from "./DateInput.module.scss";
import "./DateStyles.css";
import inputStyles from "./Input.module.scss";
import { getDatePickerFormattedDate } from "../utils/dateUtils";

export type TDateOrNull = Date | null;
type TDateOrUndefind = Date | undefined;

interface IDateInput<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	controlSize?: "n" | "sm";
	wrapperStyle?: CSSProperties;
	label?: string;
	placeholder?: string;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onChange?: (value: string) => void;
	minDate?: Date;
	maxDate?: Date;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	popperPlacement?: any;
	popperStrategy?: "absolute" | "fixed";
}

export const DateInput = <T extends FieldValues>({
	controlSize = "n",
	wrapperStyle,
	name,
	control,
	label,
	placeholder = "Default placeholder",
	onFocus,
	onChange,
	minDate,
	maxDate,
	popperPlacement,
	popperStrategy,
	...rest
}: IDateInput<T>) => {
	const { field, fieldState } = useController({ name, control });
	const [startDate] = useState<TDateOrUndefind>(new Date());

	const onDateChange = (data: TDateOrNull) => {
		const formattedDate = getDatePickerFormattedDate(data);
		field.onChange(formattedDate);

		if (typeof onChange === "function") {
			onChange(formattedDate);
		}
	};

	const selected = field.value ? new Date(field.value) : null;
	const { isDirty, invalid } = fieldState;

	const innerTernary = field.value && isDirty ? "success" : "none";
	const inputStatus = invalid ? "error" : innerTernary;

	return (
		<div className={styles.main}>
			{label && <div className={styles.label}>{label}</div>}
			<div className={clx({ [styles.root]: true, [inputStyles.small]: controlSize == "sm" })} style={wrapperStyle} {...rest}>
				<DatePicker
					className={clx({
						[styles.input]: true,
						[styles.inputError]: inputStatus === "error",
						[inputStyles.small]: controlSize == "sm",
					})}
					calendarClassName={styles.test}
					placeholderText={placeholder}
					selected={selected}
					startDate={startDate}
					// minDate={startDate}
					onChange={onDateChange}
					onFocus={onFocus}
					dateFormat="MM/dd/yyyy"
					todayButton="Today"
					// locale="en-US" //experimental
					// showWeekNumbers //experimental
					minDate={minDate}
					maxDate={maxDate}
					popperPlacement={popperPlacement}
					popperProps={popperStrategy ? { strategy: popperStrategy } : undefined}
				/>
				<div className={styles.iconHolder}>
					<CalendarIcon />
				</div>
			</div>
		</div>
	);
};
